import humanize, { OptionalLanguages } from "humanize-duration";

import {
  LocaleToShow,
  IUnitsChartData,
  IUnitsChartValue,
  IUnitsDataBase,
  UnitsCategory,
} from "../App/Analytics/Common";
import { CourseComponentType } from "../components/course-author/types";
import { getPercentage, humanizeFormat } from "../utils";
import colors from "../styles/colors";

export interface IBrushChartData {
  time: number;
  students: number;
  filtered?: number;
}

export interface IUnitInstanceAnalyticsGraphDataBase {
  correct: number;
  incorrect: number;
  skipped: number;
  totalResponses: number;
}

export enum UnitInstanceAnalyticsGraphDataKey {
  AllUsers = "All users",
  Filtered = "Filtered users",
  Unfiltered = "No Filter",
  AllLanguages = "All Languages",
  SelectedLanguages = "Selected Language",
}

export interface IUnitInstanceAnalyticsGraphData extends IUnitInstanceAnalyticsGraphDataBase {
  answersByQuestion?: Record<string, IUnitInstanceAnalyticsGraphDataBase>;
  key: UnitInstanceAnalyticsGraphDataKey;
  name?: string;
  locale?: LocaleToShow;
  identifier: string;
}

interface ISeriesData {
  y: number;
  pointWidth: number;
}

export const createUnitGraphSeries = (seriesData: IUnitsChartValue) => {
  return {
    color: createGradient(seriesData.color, seriesData.gradientColor),
    data: Array.isArray(seriesData.percentage)
      ? seriesData.percentage.map(y => ({ y }))
      : [seriesData?.percentage ?? 0],
    name: seriesData.name,
    type: "bar",
  } as Highcharts.SeriesBarOptions;
};

export const generateUnitsChartData = (allData: IUnitsDataBase[]): IUnitsChartData => {
  const datum: IUnitsChartData = {
    Correct: {
      amount: allData.map(d => d.Correct.amount),
      color: colors.chartGreen100,
      gradientColor: colors.chartGreen70,
      name: UnitsCategory.Correct,
      percentage: allData.map(d => d.Correct.percentage),
    },
    "Didn't Know": {
      amount: allData.map(d => d["Didn't Know"].amount),
      color: colors.chartLightGrey100,
      gradientColor: colors.chartLightGrey70,
      name: UnitsCategory.DidntKnow,
      percentage: allData.map(d => d["Didn't Know"].percentage),
    },
    Incorrect: {
      amount: allData.map(d => d.Incorrect.amount),
      color: colors.chartDarkBlue70,
      gradientColor: colors.chartDarkBlue100,
      name: UnitsCategory.Incorrect,
      percentage: allData.map(d => d.Incorrect.percentage),
    },
  };

  return datum;
};

export const generateSeries = (a1: number[], a2: number[]): ISeriesData[] =>
  a1.map((x, i) => ({
    y: x,
    /* tslint:disable-next-line */
    pointWidth: a2[i],
  }));

export const createGradient = (defaultColor: string, gradientColor: string) =>
  ({
    linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
    stops: [
      [0, defaultColor],
      [1, gradientColor],
    ],
  } as Highcharts.GradientColorObject);

export const calculateWidth = (assignments: number, totalAssessments: number, totalHeight: number) => {
  if (totalAssessments === 0) {
    return 0;
  }
  return (assignments / totalAssessments) * totalHeight;
};

export const timeTakenFormatter = (time: number) =>
  humanize<"shortEng">(time, {
    delimiter: " ",
    language: "shortEng",
    languages: {
      shortEng: { h: _ => "h", m: _ => "m", s: _ => "s" },
    } as OptionalLanguages<"shortEng">,
    round: true,
    ...humanizeFormat(time),
  });

export const timeTakenTooltipFormatter = (
  timeValue: number,
  label: string | number,
  textColor: string,
  isFiltered?: boolean
): string => {
  const type = isFiltered ? "Filtered" : "Average Time";
  const timeInMilliseconds = timeValue * 1000;
  const timeTaken = timeTakenFormatter(timeInMilliseconds);
  const timeMessage = `<span style="color: ${textColor};">${type}: ${timeTaken}</span>`;
  return `${label} <br> ${timeMessage}`;
};

const lessonsFormatter = (maxStudents: number) => (students: number) =>
  `${getPercentage(students, maxStudents)}% (${students})`;

export const lessonsScaleFormatter = (lessons: number) => (lessons >= 7 ? "7+" : String(lessons));

export const lessonsTooltipFormatter = (
  value: number,
  maxStudents: number,
  label: string | number,
  textColor: string,
  isFiltered?: boolean
) => {
  const type = isFiltered ? "Filtered" : "Learners";
  const percentageMessage = `<span style="color: ${textColor};">${type}: ${lessonsFormatter(maxStudents)(
    value
  )}</span>`;
  return `${label} <br> ${percentageMessage}`;
};

export const exerciseTypeFormatter = (type: CourseComponentType) => {
  switch (type) {
    case CourseComponentType.Choice:
      return "choice question";
    case CourseComponentType.ImageMap:
      return "image map question";
    case CourseComponentType.Cloze:
      return "gap sentence question";
    case CourseComponentType.ListWithOptions:
      return "order items question";
    case CourseComponentType.ListWithoutOptions:
      return "list items (in order) question";
    case CourseComponentType.UnorderedList:
      return "list items question";
    default:
      return "";
  }
};

export const labelColor = (color: any) => `<span style="color:${color}">\u25CF</span> `;

export const defaultBarTooltip = (color: any, key: string | undefined, percentage: number, y: number): string => {
  return labelColor(color) + key + ": <strong>" + Math.round(percentage) + "% / " + y + "</b>";
};
